import {
  Answer,
  PageContentApiP1Response,
  Question,
} from "../../../../../../api/services/pageContent/models";
import { ReferenceAnswer } from "../../../../../../api/services/references/models";
import {
  EMPTY_VALUE_DASH,
  NEXT_PERSONA_TYPE,
  NEXT_QUESTION_IDS,
  SELECTION_TYPE,
} from "../../../../../../helpers/constants";
import { isEmpty } from "../../../../../../helpers/utils";
import { EmailNotifications } from "../../../../../participants/components/modelsAndSchemas/Models";
import { ParticipantAnswer } from "../../../../modelsAndSchemas/Models";

export interface QuestionWithSelectedAnswers extends Question {
  selectedAnswers: Answer[];
}

const extractAnswerIdsForQuestion = (
  questionIdPattern: string,
  aboutParticipantNext: ParticipantAnswer[],
): string[] => {
  const question = aboutParticipantNext.find((q) =>
    q.questionId.includes(questionIdPattern),
  );
  return question?.answerId ?? [];
};

const enrichQuestionWithAnswers = (
  allQuestions: Question[],
  questionIdPattern: string,
  fetchedAnswers: ReferenceAnswer[],
): void => {
  const question = allQuestions.find((q) =>
    q.QuestionId.includes(questionIdPattern),
  );

  if (question) {
    // Merge fetched answers with existing (if any)
    question.AnswerList = [...question.AnswerList, ...fetchedAnswers];
  }
};

export const mapPersonaQuestions = async (
  nextPersona: string,
  pageContentData: PageContentApiP1Response,
  aboutParticipantNextData: ParticipantAnswer[],
  preferredLanguage: string,
  fetchReferenceData: (params: {
    ids: string[];
    locale: string;
  }) => Promise<ReferenceAnswer[]>,
): Promise<QuestionWithSelectedAnswers[]> => {
  const personaKey =
    nextPersona === NEXT_PERSONA_TYPE.executive
      ? NEXT_PERSONA_TYPE.executiveQuestion
      : NEXT_PERSONA_TYPE.jobSeekerQuestion;

  const components = pageContentData?.Pages?.[2]?.Components;
  const allQuestions = components?.[personaKey]?.QuestionList ?? [];
  const aboutParticipantNext = aboutParticipantNextData ?? [];

  const industryIds = extractAnswerIdsForQuestion(
    NEXT_QUESTION_IDS.SECTOR_INTEREST,
    aboutParticipantNext,
  );
  const jobTypeIds = extractAnswerIdsForQuestion(
    NEXT_QUESTION_IDS.JOB_TYPE_INTEREST,
    aboutParticipantNext,
  );

  // Batch fetch if IDs exist
  const hasIdsToFetch = industryIds.length > 0 || jobTypeIds.length > 0;
  if (hasIdsToFetch) {
    const allIds = [...industryIds, ...jobTypeIds];
    const fetchedAnswers = await fetchReferenceData({
      ids: allIds,
      locale: preferredLanguage,
    });

    // Helper to create default answer for missing IDs
    const createDefaultAnswer = (id: string): ReferenceAnswer => ({
      AnswerId: id,
      AnswerText: id,
      ItemID: id,
      ResultPageTitle: null,
      IsQuestionTitleAppended: false,
    });

    // Separate by question type (industry vs job) and add defaults for missing
    const matchedIndustryAnswers = fetchedAnswers.filter((ans) =>
      industryIds.includes(ans.AnswerId),
    );
    const missingIndustryIds = industryIds.filter(
      (id) => !fetchedAnswers.some((ans) => ans.AnswerId === id),
    );
    const industryAnswers = [
      ...matchedIndustryAnswers,
      ...missingIndustryIds.map(createDefaultAnswer),
    ];

    const matchedJobAnswers = fetchedAnswers.filter((ans) =>
      jobTypeIds.includes(ans.AnswerId),
    );
    const missingJobIds = jobTypeIds.filter(
      (id) => !fetchedAnswers.some((ans) => ans.AnswerId === id),
    );
    const jobAnswers = [
      ...matchedJobAnswers,
      ...missingJobIds.map(createDefaultAnswer),
    ];

    // Inject into question's AnswerList
    enrichQuestionWithAnswers(
      allQuestions,
      NEXT_QUESTION_IDS.SECTOR_INTEREST,
      industryAnswers,
    );
    enrichQuestionWithAnswers(
      allQuestions,
      NEXT_QUESTION_IDS.JOB_TYPE_INTEREST,
      jobAnswers,
    );
  }

  // join and filter Candidate API answers with Sitecore questions
  const mappedQuestions = allQuestions.map((q: Question) => {
    const participantAnswer = aboutParticipantNext.find(
      (p: { questionId: string }) => p.questionId === q.QuestionId,
    );

    const selectedAnswers =
      participantAnswer?.answerId
        ?.map((aid: string) => {
          let answer = q.AnswerList.find((ans) => ans.AnswerId === aid);
          if (!answer) {
            answer = q.AnswerList.find((ans) => ans.AnswerId.endsWith(aid));
          }
          return answer;
        })
        .filter((ans): ans is Answer => !!ans) || [];

    return { ...q, selectedAnswers };
  });
  // Both function Check if it's multiSelect or single select
  const singleSelectQuestions = mappedQuestions.filter(
    (q: { QuestionDesignType: string }) =>
      q.QuestionDesignType !== SELECTION_TYPE.multiSelect,
  );

  const multiSelectQuestions = mappedQuestions.filter(
    (q: { QuestionDesignType: string }) =>
      q.QuestionDesignType === SELECTION_TYPE.multiSelect,
  );

  //Merge → single first, then multi
  return [...singleSelectQuestions, ...multiSelectQuestions];
};

export const getSmsNotificationOption = (
  isSMSNotificationEnabled: boolean,
  isEmailNotificationEnabled: boolean,
  number?: string,
) => {
  if (isSMSNotificationEnabled || !isEmailNotificationEnabled) {
    return [
      {
        label: number ?? EMPTY_VALUE_DASH,
        value: number ?? EMPTY_VALUE_DASH,
      },
    ];
  }

  return [{ label: EMPTY_VALUE_DASH, value: EMPTY_VALUE_DASH }];
};

export const getSmsNotification = (
  isSMSNotificationEnabled: boolean,
  isEmailNotificationEnabled: boolean,
  number: string,
) => {
  if (isSMSNotificationEnabled || !isEmailNotificationEnabled) {
    return isEmpty(number) ? EMPTY_VALUE_DASH : number;
  }

  return EMPTY_VALUE_DASH;
};

export const getIsPrimaryEmail = (
  email: string,
  emailNotifications: EmailNotifications[] = [],
): boolean => {
  const found = emailNotifications.find(
    (item) => item.email === email && item.isPrimary,
  );
  return !!found;
};

export const getSelectedEmailNotifications = (
  emailNotifications: EmailNotifications[] = [],
) => {
  return emailNotifications.map((item) => ({
    label: item.email,
    value: item.email,
  }));
};
